import { useEffect, useState, type ReactNode } from 'react'

import { ApiError } from '@/api/client'
import { profileApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { ProfileEnhanceKind, ProfileEnhanceRequest } from '@/types/api'

/** What the rewrite is told about the field besides the text itself. */
export type EnhanceContext = Omit<ProfileEnhanceRequest, 'kind' | 'text'>

interface EnhanceToolbarProps {
  kind: ProfileEnhanceKind
  /** The field's current text; the rewrite starts from this. */
  text: string
  context: EnhanceContext
  /** Put the rewritten text into the field (nothing is saved by the call). */
  onResult: (text: string) => void
  disabled?: boolean
  /** The field is being rewritten: disable it while this is true. */
  onBusyChange?: (busy: boolean) => void
  /** A one-line note shown under the buttons until the next enhance or edit. */
  hint?: ReactNode
  className?: string
  /** `xs` under a description inside a card; `sm` under a form field. */
  size?: 'xs' | 'sm'
}

/**
 * "Enhance with AI" under a text field. The first rewrite turns the button into
 * "Regenerate"; an Undo link brings the previous text back for as long as the
 * rewrite is still what is in the field. A result that came back unchanged
 * says why, inline, instead of a toast.
 */
export function EnhanceToolbar({
  kind,
  text,
  context,
  onResult,
  disabled,
  onBusyChange,
  hint,
  className,
  size = 'sm',
}: EnhanceToolbarProps) {
  const [loading, setLoading] = useState(false)
  const [previous, setPrevious] = useState<string | null>(null)
  const [enhanced, setEnhanced] = useState<string | null>(null)
  const [runs, setRuns] = useState(0)
  const [note, setNote] = useState<{ text: string; tone: 'muted' | 'danger' } | null>(null)

  // Typing after a rewrite dismisses its note; Undo stays only while the rewrite is intact.
  useEffect(() => {
    if (enhanced !== null && text !== enhanced) {
      setNote(null)
      setPrevious(null)
      setEnhanced(null)
    }
  }, [text, enhanced])

  const setBusy = (busy: boolean) => {
    setLoading(busy)
    onBusyChange?.(busy)
  }

  const run = async () => {
    if (loading || !text.trim()) return
    setNote(null)
    setBusy(true)
    try {
      const result = await profileApi.enhance({ kind, text, ...context })
      if (result.changed && result.text.trim() && result.text !== text) {
        setRuns((n) => n + 1)
        setPrevious(text)
        setEnhanced(result.text)
        onResult(result.text)
      } else {
        setNote({
          text: result.reason ?? 'The text already reads well; nothing was changed.',
          tone: 'muted',
        })
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Could not reach the writer'
      setNote({ text: message, tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const undo = () => {
    if (previous === null) return
    const restore = previous
    setPrevious(null)
    setEnhanced(null)
    setNote(null)
    onResult(restore)
  }

  const canUndo = previous !== null && enhanced !== null && text === enhanced
  const empty = !text.trim()

  return (
    <div className={cn('mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5', className)}>
      <Button
        size={size}
        variant="secondary"
        loading={loading}
        disabled={disabled || empty}
        onClick={run}
        icon={<SparkleIcon />}
        title={empty ? 'Write something first' : undefined}
        className="text-brand hover:text-brand"
      >
        {loading ? 'Rewriting…' : runs > 0 ? 'Regenerate' : 'Enhance with AI'}
      </Button>
      {canUndo && !loading && (
        <Button size={size} variant="ghost" onClick={undo}>
          Undo
        </Button>
      )}
      {note ? (
        <p
          role={note.tone === 'danger' ? 'alert' : undefined}
          className={cn('ml-1 min-w-0 text-[12.5px] leading-snug', note.tone === 'danger' ? 'text-danger' : 'text-ink-muted')}
        >
          {note.text}
        </p>
      ) : (
        hint && !loading && <p className="ml-1 min-w-0 text-[12.5px] leading-snug text-ink-muted">{hint}</p>
      )}
    </div>
  )
}

export function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={cn('h-3.5 w-3.5', className)} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 1.5 9.6 6.4 14.5 8l-4.9 1.6L8 14.5 6.4 9.6 1.5 8l4.9-1.6L8 1.5Z"
        fill="currentColor"
      />
      <path d="M12.8 1.2l.5 1.5 1.5.5-1.5.5-.5 1.5-.5-1.5-1.5-.5 1.5-.5.5-1.5Z" fill="currentColor" opacity="0.7" />
    </svg>
  )
}
